import {
  ACCESS_NP_REVIEW_ROUTE,
  CANCELLATION_FEES_ROUTE,
  MANAGE_PRACTICE_ROUTE,
  MESSAGES_ROUTE,
  NAVBAR_ROUTES,
  PRESCRIPTION_REVIEW_ROUTE,
  PRESCRIPTION_TASKS_ROUTE,
  SUPERVISION_TASKS_ROUTE,
  TASKS_ROUTE,
} from '@web-app/components/consts';
import { CollapsibleNavItem } from '@web-app/components/Navbar/CollapsibleNavItem';
import { NavLink } from '@web-app/components/Navbar/NavLink';
import {
  FeatureFlags,
  NavbarMenuItemsProps,
  NavRoute,
} from '@web-app/components/Navbar/types';
import { shouldShowRoute } from '@web-app/components/Navbar/utils';
import { Badge } from '@web-app/components/ui/Badge/badge';

export const NavbarMenuItems = ({
  settingsPageAccess,
  managePracticeDashboard,
  calendarEnabled,
  performanceDashboard,
  showInternalTools,
  isSupervisor,
  supervisionTasks,
  isBerryStreetOnly,
  groupPracticeInvoiceEnabled,
  w9PageEnabled,
  confirmCancellationEnabled,
  additionalBenefitsCheckEnabled,
  chatEnabled,
  navEntries,
  subOrgsInvoiceReviewEnabled,
  nonAppointmentHoursEnabled,
  inSessionTemplatesNavEnabled,
  documentsNavEnabled,
  isPrescriber,
  prescriptionTasksEnabled,
  hasAccessNpReviewQueue,
  enrollmentsCount,
  accessPendingReviewCount,
  tasksCount,
  supervisionTasksCount,
  cancellationDecisionsCount,
  prescriptionTasksCount,
  unreadLabCount,
  messagesCount,
  notificationRoutes,
}: NavbarMenuItemsProps): JSX.Element => {
  const adminParam =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('admin')
      : null;

  const featureFlags: FeatureFlags = {
    settingsPageAccess,
    managePracticeDashboard,
    calendarEnabled,
    performanceDashboard,
    showInternalTools,
    isSupervisor,
    supervisionTasks,
    isBerryStreetOnly,
    groupPracticeInvoiceEnabled,
    w9PageEnabled,
    confirmCancellationEnabled,
    additionalBenefitsCheckEnabled,
    chatEnabled,
    navEntries,
    subOrgsInvoiceReviewEnabled,
    nonAppointmentHoursEnabled,
    inSessionTemplatesNavEnabled,
    documentsNavEnabled,
    isPrescriber,
    prescriptionTasksEnabled,
    hasAccessNpReviewQueue,
  };

  const buildUrl = (path: string): string => {
    const notificationRoute = notificationRoutes[path];
    const targetPath = notificationRoute || path;
    const url = new URL(targetPath, window.location.origin);
    if (adminParam) {
      url.searchParams.set('admin', adminParam);
    }
    for (const [key, value] of Object.entries(navEntries[path]?.query ?? {})) {
      url.searchParams.set(key, value);
    }
    return url.href;
  };

  const countBadges: Array<{ path: string; count: number; testId: string }> = [
    {
      path: ACCESS_NP_REVIEW_ROUTE,
      count: accessPendingReviewCount,
      testId: 'care-updates-count',
    },
    {
      path: MANAGE_PRACTICE_ROUTE,
      count: enrollmentsCount,
      testId: 'enrollments-count',
    },
    { path: TASKS_ROUTE, count: tasksCount, testId: 'tasks-count' },
    {
      path: SUPERVISION_TASKS_ROUTE,
      count: supervisionTasksCount,
      testId: 'supervision-tasks-count',
    },
    {
      path: CANCELLATION_FEES_ROUTE,
      count: cancellationDecisionsCount,
      testId: 'cancellation-fees-count',
    },
    {
      path: PRESCRIPTION_TASKS_ROUTE,
      count: prescriptionTasksCount,
      testId: 'prescription-tasks-count',
    },
    {
      path: PRESCRIPTION_REVIEW_ROUTE,
      count: unreadLabCount,
      testId: 'unread-lab-count',
    },
    { path: MESSAGES_ROUTE, count: messagesCount, testId: 'messages-count' },
    ...Object.values(navEntries),
  ];

  // The badge value a route contributes to a collapsed parent group's
  // aggregate count. Notification-only routes (presence indicator) count as 1.
  const getRouteBadgeCount = (route: NavRoute): number => {
    const countMatch = countBadges.find(
      ({ path, count }) => path === route.path && count > 0,
    );
    if (countMatch) {
      return countMatch.count;
    }
    if (route.path === ACCESS_NP_REVIEW_ROUTE) return 0;
    return notificationRoutes[route.path] ? 1 : 0;
  };

  const renderIconIndicator = (route: NavRoute) =>
    navEntries[route.path]?.iconIndicator;

  const renderBadge = (route: NavRoute) => {
    const match = countBadges.find(
      ({ path, count }) => path === route.path && count > 0,
    );
    if (match) {
      return (
        <Badge variant="destructive" size="sm" data-testid={match.testId}>
          {match.count}
        </Badge>
      );
    }
    if (route.path === ACCESS_NP_REVIEW_ROUTE) return null;
    if (notificationRoutes[route.path]) {
      return (
        <Badge
          variant="destructive"
          size="sm"
          data-testid="notification-indicator"
        >
          1
        </Badge>
      );
    }
    return null;
  };

  return (
    <>
      {NAVBAR_ROUTES.map((route) => {
        if (!shouldShowRoute(route, featureFlags)) {
          return null;
        }

        // Route with children - render as collapsible
        if (route.children && route.children.length > 0) {
          // Filter visible children based on feature flags
          const visibleChildren = route.children.filter((child) =>
            shouldShowRoute(child, featureFlags),
          );

          if (visibleChildren.length === 0) {
            return null;
          }

          if (
            visibleChildren.length === 1 &&
            visibleChildren[0].path === route.path
          ) {
            return (
              <NavLink
                key={route.path}
                route={route}
                href={buildUrl(route.path)}
                badge={renderBadge(route)}
              />
            );
          }

          const notificationCount = visibleChildren.reduce(
            (sum, child) => sum + getRouteBadgeCount(child),
            0,
          );

          return (
            <CollapsibleNavItem
              key={route.path}
              route={route}
              notificationCount={notificationCount}
            >
              {visibleChildren.map((child) => (
                <NavLink
                  key={child.path}
                  route={child}
                  href={buildUrl(child.path)}
                  badge={renderBadge(child)}
                  variant="subItem"
                />
              ))}
            </CollapsibleNavItem>
          );
        }

        // Regular route - render as link
        return (
          <NavLink
            key={route.path}
            route={route}
            href={buildUrl(route.path)}
            badge={renderBadge(route)}
            iconIndicator={renderIconIndicator(route)}
          />
        );
      })}
    </>
  );
};
